import { Alert, Box, Button, Card, CardActionArea, CardContent, Divider, Typography } from "@mui/material";
import PrimaryLayout from "../../layout/Layout"
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBookingsByCustomerId, resetBookingList } from "../../redux/actions/apiActions";
import moment from "moment-timezone";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { Alarm, BusinessCenter, ConfirmationNumber, CorporateFare } from "@mui/icons-material";
import QRCode from "react-qr-code";
import SkeletonCard from "../../components/Loaders/SkeletonCard";
import { setShowLoginForm } from "../../redux/actions/authActions";
import { useRef } from "react";
import { useIsVisible } from "../../hooks/useIsVisible";
import { Config } from "../../config/regions";
import BookingDetailsCard from "./BookingDetailsCard";
import GroupedBookingCardDetails from "./GroupedBookingCards";

const BookingHistory = () => {
    const userData = useSelector(state => state.auth.userData);
    const bookingList = useSelector(state => state.api.bookingList);
    const bookingListTotalCount = useSelector(state => state.api.bookingListTotalCount);
    const bookingListLastLoadedPage = useSelector(state => state.api.bookingListLastLoadedPage);
    const bookingListLoading = useSelector(state => state.api.bookingListLoading);
    const queryParams = useSelector(state => state.common.queryParams);
    const [isLoading, setIsLoading] = useState(false);
    const [timeframeFilter, setTimeframeFilter] = useState(null);

    const pageEndRef = useRef(null);
    const isPageEndVisible = useIsVisible(pageEndRef);

    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [pastBookings, setPastBookings] = useState([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const PAGE_SIZE = 20


    const groupBookings = (bookingList) => {
        const groups = new Map();
        // First pass: build groups while preserving order
        for (const booking of bookingList) {
            const groupId = booking.parentBooking?.id || booking.id;

            if (!groups.has(groupId)) {
                groups.set(groupId, []);
            }
            groups.get(groupId).push(booking);
        }
        // Second pass: rebuild the list preserving original order
        const result = [];
        const added = new Set();
        for (const booking of bookingList) {
            const groupId = booking.parentBooking?.id || booking.id;
            if (added.has(groupId)) continue;
            const group = groups.get(groupId);
            if (group.length === 1) {
                result.push(group[0]);
            } else {
                result.push(group);
            }
            added.add(groupId);
        }
        return result;
    };

    const renderBookingCard = (booking, idx) => {
        return Array.isArray(booking) && booking.length > 1 ?
            <GroupedBookingCardDetails bookings={booking} index={idx} />
            :
            <BookingDetailsCard booking={Array.isArray(booking) && booking.length == 1 ? booking[0] : booking} index={idx} />
    }

    useEffect(() => {
        if (userData?.id) {
            dispatch(resetBookingList());
            setTimeframeFilter("upcoming");
        } else {
            //dispatch(setShowLoginForm(true));
        }
    }, [userData])

    useEffect(() => {
        //console.log("isPageEndVisible", isPageEndVisible, bookingList?.length, bookingListTotalCount);
        if (isPageEndVisible && !bookingListLoading && bookingList?.length < bookingListTotalCount && bookingListLastLoadedPage >= 0) {
            if (timeframeFilter) {
                const filter = timeframeFilter + (queryParams?.orgId ? `&orgId=${queryParams.orgId}` : '');
                dispatch(fetchBookingsByCustomerId(userData?.id, PAGE_SIZE, filter));
            }
        }
    }, [isPageEndVisible])

    useEffect(() => {
        if (bookingList) {
            //console.log(curentFilter,bookingList.length, bookingListTotalCount, bookingListLastLoadedPage );
            setIsLoading(true);
            if (timeframeFilter === "upcoming") {
                if (bookingListLastLoadedPage >= 0) {
                    const bookingList_ = bookingList.sort((a, b) => a?.startSec - b?.startSec)
                    setUpcomingBookings(groupBookings(bookingList_));
                }
                if (bookingListLastLoadedPage >= 0 && bookingList.length >= bookingListTotalCount) {
                    dispatch(resetBookingList()) //reset redux states for upcoming list
                    setTimeframeFilter("past");
                }
            } else {
                const bookingList_ = bookingList;
                setPastBookings(groupBookings(bookingList_));
            }
            setTimeout(() => setIsLoading(false), 1000);
        }
    }, [bookingList])

    useEffect(() => {
        if (timeframeFilter) {
            const filter = timeframeFilter + (queryParams?.orgId ? `&orgId=${queryParams.orgId}` : '');
            dispatch(fetchBookingsByCustomerId(userData?.id, PAGE_SIZE, filter));
        }
    }, [timeframeFilter])


    return (
        <PrimaryLayout>
            {!isLoading && !userData?.id ?
                <>
                    <Alert
                        sx={{ justifyContent: 'center', borderRadius: 3, my: 10, maxWidth: 'sm', mx: 'auto' }}
                        severity="error"
                        onClick={() => dispatch(setShowLoginForm(true))}
                        action={
                            <Button color="inherit" size="small" onClick={() => dispatch(setShowLoginForm(true))}>
                                LOGIN
                            </Button>
                        }
                    >
                        Please login to access this page
                    </Alert>
                </>
                :
                <Box className="p-2 py-3 min-h-full">
                    {!bookingList?.length && (isLoading || bookingListLoading) ?
                        <>
                            <SkeletonCard cardType="bookingTimelineHead" />
                            <SkeletonCard cardType="bookingTimeline" />
                        </>
                        :
                        <Box sx={{ maxWidth: "md", mx: 'auto' }}>
                            {upcomingBookings.length == 1 ?
                                <Box sx={{ mt: 3 }}>
                                    <Divider>Upcoming Bookings</Divider>
                                    <Box sx={{ mt: 2 }}>
                                        {renderBookingCard(upcomingBookings[0], 0)}
                                    </Box>
                                </Box>
                                : upcomingBookings.length > 1 ?
                                    <Box sx={{ mt: 3 }}>
                                        <Divider>Upcoming Bookings</Divider>
                                        <Timeline position="alternate">
                                            {upcomingBookings.map((booking, idx) =>
                                                <TimelineItem key={idx}>
                                                    <TimelineSeparator>
                                                        <TimelineDot />
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        {renderBookingCard(booking, idx)}
                                                    </TimelineContent>
                                                </TimelineItem>
                                            )}
                                        </Timeline>
                                    </Box>
                                    : null
                            }
                            {pastBookings.length == 1 ?
                                <Box sx={{ mt: 3 }}>
                                    <Divider>Past Bookings</Divider>
                                    <Box sx={{ mt: 2 }}>
                                        {renderBookingCard(pastBookings[0], 0)}
                                    </Box>
                                </Box>
                                : pastBookings.length > 1 ?
                                    <Box sx={{ mt: 3 }}>
                                        <Divider>Past Bookings</Divider>
                                        <Timeline position="alternate" >
                                            {pastBookings.map((booking, idx) =>
                                                <TimelineItem key={idx}>
                                                    <TimelineSeparator>
                                                        <TimelineDot />
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        {renderBookingCard(booking, idx)}
                                                    </TimelineContent>
                                                </TimelineItem>
                                            )}
                                        </Timeline>

                                    </Box>
                                    : null
                            }
                            {!isLoading && !bookingListLoading && !upcomingBookings.length && !pastBookings.length && bookingListLastLoadedPage >= 0 && !bookingListTotalCount ?
                                <>
                                    <Alert sx={{ justifyContent: 'center', borderRadius: 3, my: 10 }} severity="error">No bookings</Alert>
                                </>
                                : bookingListTotalCount && bookingList?.length < bookingListTotalCount ?
                                    <>
                                        <SkeletonCard cardType="bookingTimeline" />
                                    </>
                                    : null
                            }
                        </Box>
                    }
                </Box>
            }
            <div ref={pageEndRef} />
        </PrimaryLayout>
    )
}



export default BookingHistory;